import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "./ClientUserGuide.css";
import { useClientTour } from "./ClientLiveTour";

const ORG = "AccunityHR";
const ORG_LABEL = "ACC01 - AccunityHR";
const REPORT_BRANCH = "BNG01 - AccunityHR Bangalore";
const REPORT_MONTH_LABEL = "May 2026";
const BRANCH_BNG = "BNG01 - Bengaluru (Kengeri)";
const BRANCH_MYS = "BRNMYS01 - Mysuru (Hebbal)";
const BRANCHES = [BRANCH_BNG, BRANCH_MYS];
const YEARS = ["2025", "2026"];
const MONTHS = ["Jun", "Jul", "Aug"];
const STORAGE_KEY = "accunityhr.clientTour.v2";

const GUIDE_TOC = [
  { id: "login", label: "1. Sign in" },
  { id: "sidebar", label: "2. Sidebar" },
  { id: "dashboard", label: "3. Dashboard" },
  { id: "forms", label: "4. Forms" },
  { id: "reports", label: "5. Reports" },
  { id: "returns", label: "6. Returns" },
  { id: "documents", label: "7. Compliance Documents" },
  { id: "signout", label: "8. Sign out" },
];

function scrollToGuideSection(sectionId) {
  const el = document.getElementById(sectionId);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "start" });
}

function GuideToc({ onJump }) {
  return (
    <nav className="client-guide-toc" aria-label="Guide sections">
      {GUIDE_TOC.map((item) => (
        <button key={item.id} type="button" onClick={() => onJump(item.id)}>
          {item.label}
        </button>
      ))}
    </nav>
  );
}

/** Module checklist shown in the floating panel */
const CHECKLIST = [
  { id: "login", label: "Sign in", slideIds: ["login"] },
  { id: "nav", label: "Sidebar & Dashboard", slideIds: ["sidebar", "dashboard"] },
  { id: "forms", label: "Forms", slideIds: ["forms-start", "forms-branch", "forms-year", "forms-month", "forms-list", "forms-preview", "forms-actions"] },
  { id: "reports", label: "Reports", slideIds: ["reports-hub", "reports-branch", "reports-search", "reports-cards", "reports-table", "reports-period", "reports-rows"] },
  { id: "returns", label: "Returns", slideIds: ["returns-filters", "returns-list"] },
  { id: "docs", label: "Compliance Documents", slideIds: ["documents"] },
  { id: "signout", label: "Sign out", slideIds: ["signout"] },
];

/** One slide per step — PPT style + tooltip copy */
const SLIDES = [
  {
    id: "intro",
    title: "Client user guide",
    hint: "Start the interactive tour. Only the highlighted control matters on each step.",
    tooltipTitle: "Ready to explore?",
    tooltipBody: "We'll walk Forms, Reports, Returns and Documents — the same path clients use every day.",
    hotspot: "start",
  },
  {
    id: "login",
    title: "1. Sign in",
    hint: "Enter mail and password, then Get OTP.",
    tooltipTitle: "Sign in",
    tooltipBody: "Use User Mail + Password, then Get OTP. After OTP you land on Dashboard.",
    hotspot: "login-mail",
  },
  {
    id: "sidebar",
    title: "2. Sidebar",
    hint: "Left menu: Dashboard, Register (Forms / Reports), Returns, Documents.",
    tooltipTitle: "Your menu",
    tooltipBody: "Open Register for Forms and Reports. Returns and Compliance Documents sit below.",
    hotspot: "sidebar",
  },
  {
    id: "dashboard",
    title: "3. Dashboard",
    hint: "KPI tiles, Registers Overview chart, and Upcoming Holidays.",
    tooltipTitle: "Dashboard at a glance",
    tooltipBody: "Tiles show org, branches and headcount. Scroll for the chart and holidays.",
    hotspot: "dashboard-kpis",
  },
  {
    id: "forms-start",
    title: "4. Forms — start",
    hint: "Organization is locked to AccunityHR. Next: choose Branch.",
    tooltipTitle: "Forms filters",
    tooltipBody: "Org is locked. You only pick Branch, Year and Month.",
    hotspot: "forms-filters",
  },
  {
    id: "forms-branch",
    title: "4. Forms — Branch",
    hint: "Open Branch and select Bengaluru (Kengeri).",
    tooltipTitle: "Select Branch",
    tooltipBody: "Pick BNG01 - Bengaluru (Kengeri) from the list.",
    hotspot: "forms-filters",
  },
  {
    id: "forms-year",
    title: "4. Forms — Year",
    hint: "Select Year 2026.",
    tooltipTitle: "Select Year",
    tooltipBody: "Only years with generated forms appear. Choose 2026.",
    hotspot: "forms-filters",
  },
  {
    id: "forms-month",
    title: "4. Forms — Month",
    hint: "Select Month Aug, then View forms.",
    tooltipTitle: "Select Month & View",
    tooltipBody: "Choose Aug, then click View forms to load the list.",
    hotspot: "forms-filters",
  },
  {
    id: "forms-list",
    title: "4. Forms — list",
    hint: "List opens with eye and download on each row.",
    tooltipTitle: "Forms list",
    tooltipBody: "Each row has View (eye) and Download. Tick rows for bulk zip.",
    hotspot: "forms-list",
  },
  {
    id: "forms-preview",
    title: "4. Forms — View PDF",
    hint: "Eye opens Form XII preview. Close returns to the list.",
    tooltipTitle: "PDF preview",
    tooltipBody: "Eye opens the form. Close returns you to the list.",
    hotspot: "forms-preview",
  },
  {
    id: "forms-actions",
    title: "4. Forms — actions",
    hint: "Eye = preview, row download = one file, bulk = zip of selected.",
    tooltipTitle: "View · Download · Bulk",
    tooltipBody: "Eye previews one form. Row download saves one PDF. Tick 2+ for a zip.",
    hotspot: "forms-actions",
  },
  {
    id: "reports-hub",
    title: "5. Reports — hub",
    hint: "Register → Reports. Current month, Organization, and Branch sit at the top.",
    tooltipTitle: "Reports hub",
    tooltipBody: "This matches Register → Reports. Current month is the payroll period. Organization is locked for clients.",
    hotspot: "reports-hub",
  },
  {
    id: "reports-branch",
    title: "5. Reports — Branch",
    hint: "Open Branch and choose AccunityHR Bangalore. Cards stay locked until a branch is selected.",
    tooltipTitle: "Select Branch",
    tooltipBody: "Pick BNG01 - AccunityHR Bangalore. Report cards unlock after branch is chosen.",
    hotspot: "reports-branch",
  },
  {
    id: "reports-search",
    title: "5. Reports — search",
    hint: "Search reports by name, code, or category. Available reports shows the count.",
    tooltipTitle: "Search reports",
    tooltipBody: "Type a report name, code, or category. Available reports updates as you filter.",
    hotspot: "reports-search",
  },
  {
    id: "reports-cards",
    title: "5. Reports — MONTHLY INPUTS",
    hint: "Reports are grouped by category. Open Employee Master, Salary Register, or CTC Master.",
    tooltipTitle: "Report cards",
    tooltipBody: "MONTHLY INPUTS holds payroll registers. Open a card to view that report.",
    hotspot: "reports-cards",
  },
  {
    id: "reports-table",
    title: "5. Reports — table",
    hint: "The report opens for the current month with rows for the selected branch.",
    tooltipTitle: "Report table",
    tooltipBody: "Employee Master (or any card) opens a table. Year and Month sit in the header.",
    hotspot: "reports-table",
  },
  {
    id: "reports-period",
    title: "5. Reports — Year & Month",
    hint: "Use Year then Month to change the payroll period — same filters as live Reports.",
    tooltipTitle: "Payroll month",
    tooltipBody: "Choose Year 2026, then Month May. The table reloads for that payroll month.",
    hotspot: "reports-period",
  },
  {
    id: "reports-rows",
    title: "5. Reports — search rows",
    hint: "Search employees in the table by name or code.",
    tooltipTitle: "Search rows",
    tooltipBody: "Use Search here… to find an employee, for example Asha.",
    hotspot: "reports-rows",
  },
  {
    id: "returns-filters",
    title: "6. Returns — filters",
    hint: "Same as Forms: Branch → Year → Month → View documents.",
    tooltipTitle: "Returns filters",
    tooltipBody: "Same pattern as Forms. Button label is View documents.",
    hotspot: "returns-filters",
  },
  {
    id: "returns-list",
    title: "6. Returns — list",
    hint: "PF Return and ESIC Return with eye and download.",
    tooltipTitle: "Returns list",
    tooltipBody: "Preview or download PF and ESIC returns. Bulk zip works the same way.",
    hotspot: "returns-list",
  },
  {
    id: "documents",
    title: "7. Compliance Documents",
    hint: "Trade licence, PF challan, ESIC challan — view and download only.",
    tooltipTitle: "Compliance Documents",
    tooltipBody: "Licences and challans. Clients can view and download only.",
    hotspot: "documents",
  },
  {
    id: "signout",
    title: "8. Sign out",
    hint: "Use the profile menu in the top bar.",
    tooltipTitle: "You're ready",
    tooltipBody: "Sign out from the profile menu. Finish to open the full guide anytime.",
    hotspot: "signout",
  },
];

function loadTourState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function saveTourState(partial) {
  try {
    const prev = loadTourState() || {};
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...prev, ...partial }));
  } catch {
    /* ignore */
  }
}

function Screen({ title, caption, children, hotspotId, activeHotspot }) {
  const lit = hotspotId && hotspotId === activeHotspot;
  return (
    <figure className={"client-guide-figure" + (lit ? " is-tour-screen" : "")}>
      <div className={"ug-screen" + (lit ? " ug-hotspot-wrap" : "")} role="img" aria-label={caption}>
        {lit ? <span className="ug-beacon" aria-hidden="true" /> : null}
        <div className="ug-chrome">
          <span className="ug-dot" />
          <span className="ug-dot" />
          <span className="ug-dot" />
          <span>{title}</span>
        </div>
        {children}
      </div>
      <figcaption>{caption}</figcaption>
    </figure>
  );
}

function AppFrame({ active, children }) {
  return (
    <div className="ug-body">
      <aside className="ug-side">
        <strong>AccunityHR</strong>
        <span className={active === "dash" ? "is-on" : ""}>Dashboard</span>
        <span className={active === "forms" || active === "reports" ? "is-on" : ""}>Register</span>
        <span className={"pad " + (active === "forms" ? "is-on" : "")}>Forms</span>
        <span className={"pad " + (active === "reports" ? "is-on" : "")}>Reports</span>
        <span className={active === "returns" ? "is-on" : ""}>Returns</span>
        <span className={active === "docs" ? "is-on" : ""}>Compliance Documents</span>
        <span className={active === "guide" ? "is-on" : ""}>User Guide</span>
      </aside>
      <div className="ug-main">{children}</div>
    </div>
  );
}

function ReportsHubMock({ spot, branchPicked = false, branchOpen = false, searchText = "" }) {
  const blink = (id) => (spot === id ? " ug-blink-target" : "");
  return (
    <AppFrame active="reports">
      <div className="ug-rhub" aria-hidden="true">
        <div className={"ug-rhub-hero" + blink("hub")}>
          <div className="ug-rhub-head">
            <h4>Reports</h4>
            <div className="ug-rhub-month">
              <span className="ug-rhub-live">Current month</span>
              <span className="ug-rhub-pill">{REPORT_MONTH_LABEL}</span>
            </div>
          </div>
          <div className="ug-rhub-scope">
            <label>
              <span>Organization</span>
              <div className="ug-rhub-select is-locked">{ORG_LABEL}</div>
            </label>
            <label className={blink("branch")}>
              <span>Branch</span>
              <div className={"ug-rhub-select" + (branchOpen ? " is-open" : "")}>
                {branchPicked ? REPORT_BRANCH : "Select branch"}
                <span className="caret">▾</span>
              </div>
              {branchOpen ? (
                <ul className="ug-menu">
                  <li className="is-pick">{REPORT_BRANCH}</li>
                </ul>
              ) : null}
            </label>
          </div>
          <div className={"ug-rhub-toolbar" + blink("search")}>
            <div className="ug-rhub-search">
              {searchText || "Search reports by name, code, or category..."}
            </div>
            <div className="ug-rhub-count">
              <em>Available reports</em>
              <b>{searchText ? "1" : "17"}</b>
            </div>
          </div>
        </div>
        <div className={"ug-rhub-section" + (branchPicked ? "" : " is-locked") + blink("cards")}>
          <div className="ug-rhub-section-head">
            <div className="ug-rhub-icon" />
            <div>
              <strong>MONTHLY INPUTS</strong>
              <p>Payroll registers, attendance, leave, and monthly employee inputs.</p>
            </div>
            <span>13 reports</span>
          </div>
          <div className="ug-rhub-grid">
            <div className={"ug-rhub-card" + (spot === "cards" ? " is-on" : "")}>
              <b>Employee Master</b>
              <p>All active employees by default. Use month to see who was on payroll or joined that month.</p>
              <em>employee-master</em>
            </div>
            <div className="ug-rhub-card">
              <b>Salary Register</b>
              <p>Earnings, deductions, and net pay by employee for the month.</p>
              <em>salary-register</em>
            </div>
            <div className="ug-rhub-card">
              <b>CTC Master</b>
              <p>Cost-to-company structure and components by employee.</p>
              <em>ctc-master</em>
            </div>
          </div>
        </div>
      </div>
    </AppFrame>
  );
}

function ReportsTableMock({ spot, yearOpen = false, monthOpen = false, searchText = "", filterAsha = false }) {
  const blink = (id) => (spot === id ? " ug-blink-target" : "");
  const rows = [
    { code: "E001", name: "Asha Rao", branch: "BNG01", dept: "Marketing" },
    { code: "E002", name: "Vikram Shah", branch: "BNG01", dept: "Marketing" },
    { code: "E003", name: "Neha Patel", branch: "BNG01", dept: "Marketing" },
  ].filter((r) => !filterAsha || r.name.toLowerCase().includes("asha"));
  return (
    <AppFrame active="reports">
      <div className="ug-rtable" aria-hidden="true">
        <div className="ug-rtable-crumb">Reports / Employee Master</div>
        <div className={"ug-rtable-head" + blink("table")}>
          <div>
            <h4>Employee Master</h4>
            <p>
              {REPORT_BRANCH} · {REPORT_MONTH_LABEL}
            </p>
          </div>
        </div>
        <div className="ug-rtable-toolbar">
          <div className={"ug-rtable-search" + blink("rows")}>
            {searchText || "Search here..."}
          </div>
          <div className={"ug-rtable-period" + blink("period")}>
            <div className={"ug-rhub-select" + (yearOpen ? " is-open" : "")}>
              2026<span className="caret">▾</span>
            </div>
            {yearOpen ? (
              <ul className="ug-menu ug-menu-period">
                <li className="is-pick">2026</li>
              </ul>
            ) : null}
            <div className={"ug-rhub-select" + (monthOpen ? " is-open" : "")}>
              May<span className="caret">▾</span>
            </div>
            {monthOpen ? (
              <ul className="ug-menu ug-menu-period ug-menu-period-month">
                <li className="is-pick">May</li>
              </ul>
            ) : null}
          </div>
          <span className="ug-rtable-entries">Total Entries : {rows.length}</span>
        </div>
        <table className="ug-mini-table">
          <thead>
            <tr>
              <th>Employee Code</th>
              <th>Employee Name</th>
              <th>Branch ID</th>
              <th>Department</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.code}>
                <td>{r.code}</td>
                <td>{r.name}</td>
                <td>{r.branch}</td>
                <td>{r.dept}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AppFrame>
  );
}

function OrgCard() {
  return (
    <div className="ug-org is-locked">
      <span>Organization</span>
      <strong>{ORG}</strong>
      <em>The client cannot be changed because you are currently logged in as the client.</em>
    </div>
  );
}

function SelectField({ label, value, open, options, pick }) {
  return (
    <div className="ug-select-wrap">
      <label>{label}</label>
      <div className={"ug-select" + (open ? " is-open" : "")}>
        <span>{value}</span>
        <span className="caret">▾</span>
      </div>
      {open ? (
        <ul className="ug-menu">
          {options.map((item) => (
            <li key={item} className={item === pick ? "is-pick" : ""}>
              {item}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function FilterBar({ active, open, submit = "View forms" }) {
  return (
    <div className={"ug-filter" + (open ? " has-open" : "")}>
      <SelectField
        label="Branch"
        value={
          active === "branch" || ["year", "month", "ready", "list"].includes(active)
            ? BRANCH_BNG
            : "Select branch"
        }
        open={open === "branch"}
        options={BRANCHES}
        pick={BRANCH_BNG}
      />
      <SelectField
        label="Year"
        value={
          ["month", "ready", "list"].includes(active) || open === "month" ? "2026" : "Select year"
        }
        open={open === "year"}
        options={YEARS}
        pick="2026"
      />
      <SelectField
        label="Month"
        value={active === "ready" || active === "list" ? "Aug" : "Select month"}
        open={open === "month"}
        options={MONTHS}
        pick="Aug"
      />
      <div className="ug-btn" style={{ width: 128, flex: "0 0 128px" }}>
        {submit}
      </div>
    </div>
  );
}

function ListHead({ title, count, bulkOn }) {
  return (
    <div className="ug-list-head">
      <strong>{title}</strong>
      <div className="ug-list-tools">
        <span className={"ug-bulk" + (bulkOn ? " is-on" : "")} title="Bulk download">
          ⬇
        </span>
        <span className="ug-count">{count}</span>
      </div>
    </div>
  );
}

function Check({ on }) {
  return <span className={"ug-check" + (on ? " on" : "")} />;
}

function ActionIcons() {
  return (
    <span className="ug-actions">
      <span className="ug-ico" title="View">
        👁
      </span>
      <span className="ug-ico" title="Download">
        ⬇
      </span>
    </span>
  );
}

function WelcomeModal({ onStart, onSkip }) {
  return (
    <div className="tour-modal-layer" role="dialog" aria-modal="true" aria-labelledby="tour-welcome-title">
      <div className="tour-modal">
        <span className="tour-modal-eyebrow">ACCUNITYHR · CLIENT TOUR</span>
        <h2 id="tour-welcome-title">Start your in-app tour</h2>
        <p>
          We will walk the real Client portal: Dashboard, Forms, Reports, Returns and
          Compliance Documents — with tooltips, hotspots and a checklist. If a list is empty,
          labelled demo samples appear so you still see the flow.
        </p>
        <ul className="tour-modal-list">
          <li>Tooltips on real sidebar and page controls</li>
          <li>Pulsing hotspots on the next step</li>
          <li>Checklist progress · demo fallback when no data</li>
        </ul>
        <div className="tour-modal-actions">
          <button type="button" className="ghost" onClick={onSkip}>
            Read guide only
          </button>
          <button type="button" className="primary" onClick={onStart}>
            Start tour
          </button>
        </div>
      </div>
    </div>
  );
}

function DoneModal({ onRestart, onFullGuide, onClose }) {
  return (
    <div className="tour-modal-layer" role="dialog" aria-modal="true" aria-labelledby="tour-done-title">
      <div className="tour-modal">
        <div className="tour-done-check">✓</div>
        <span className="tour-modal-eyebrow">TOUR COMPLETE</span>
        <h2 id="tour-done-title">You&apos;re ready</h2>
        <p>
          Use the same flow with live data from the sidebar: Forms, Reports, Returns and
          Documents.
        </p>
        <div className="tour-modal-actions">
          <button type="button" className="ghost" onClick={onRestart}>
            Restart tour
          </button>
          <button type="button" className="ghost" onClick={onFullGuide}>
            Full guide
          </button>
          <button type="button" className="primary" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

function TourTooltip({ title, body, stepLabel, onBack, onNext, onSkip, nextLabel, backDisabled }) {
  return (
    <div className="tour-tooltip" role="status">
      <div className="tour-tooltip-caret" />
      <div className="tour-tooltip-head">
        <span className="tour-tooltip-step">{stepLabel}</span>
        <strong>{title}</strong>
      </div>
      <p>{body}</p>
      <div className="tour-tooltip-actions">
        <button type="button" className="ghost" disabled={backDisabled} onClick={onBack}>
          Back
        </button>
        <button type="button" className="ghost" onClick={onSkip}>
          Skip
        </button>
        <button type="button" className="primary" onClick={onNext}>
          {nextLabel}
        </button>
      </div>
    </div>
  );
}

function TourChecklist({ items, activeModuleId, onJump, collapsed, onToggle }) {
  const done = items.filter((i) => i.status === "done").length;
  return (
    <aside className={"tour-checklist" + (collapsed ? " is-collapsed" : "")}>
      <button type="button" className="tour-checklist-toggle" onClick={onToggle}>
        <span>
          Progress · {done}/{items.length}
        </span>
        <span aria-hidden="true">{collapsed ? "▴" : "▾"}</span>
      </button>
      {!collapsed && (
        <ul>
          {items.map((item) => (
            <li key={item.id} className={item.id === activeModuleId ? "is-active" : ""}>
              <button type="button" onClick={() => onJump(item.id)}>
                <span className={"tour-check-dot " + item.status}>
                  {item.status === "done" ? "✓" : item.status === "current" ? "●" : "○"}
                </span>
                <span>{item.label}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </aside>
  );
}

/* ---------- SLIDE CONTENT ---------- */

function SlideIntro({ onStart, activeHotspot, onJump }) {
  return (
    <section className="client-guide-section slide-panel">
      <h2>Client user guide</h2>
      <p>
        Demo walkthrough for AccunityHR. Organization is always locked as
        <strong> AccunityHR</strong>. You choose Branch, Year, and Month, then open the list
        and use View, Download, or bulk download.
      </p>
      <p>Follow the tooltip and pulsing hotspot. Use the checklist to jump modules.</p>
      <div className="client-guide-toolbar" style={{ marginTop: 16 }}>
        <span className={activeHotspot === "start" ? "ug-hotspot-wrap" : ""}>
          {activeHotspot === "start" ? <span className="ug-beacon" /> : null}
          <button type="button" className="ug-blink-target" onClick={onStart}>
            Start tour
          </button>
        </span>
      </div>
      <GuideToc onJump={onJump} />
    </section>
  );
}

function SlideLogin({ activeHotspot }) {
  return (
    <section className="client-guide-section slide-panel" id="login">
      <h2>1. Sign in</h2>
      <ol>
        <li>Open AccunityHR and go to the login page.</li>
        <li>
          Enter your <strong>User Mail</strong> and <strong>Password</strong>.
        </li>
        <li>
          Select <strong>Get OTP</strong> and complete verification.
        </li>
        <li>
          You land on Dashboard. The top bar shows <strong>AccunityHR</strong>.
        </li>
      </ol>
      <Screen
        title="Login"
        caption="Figure 1. AccunityHR login — User Mail, Password, and Get OTP."
        hotspotId="login-mail"
        activeHotspot={activeHotspot}
      >
        <div className="ug-login">
          <div className="ug-login-copy">
            <h4>AccunityHR</h4>
            <p>Simplify &amp; Manage Compliance In-House</p>
          </div>
          <div className="ug-login-card">
            <div className={"ug-field" + (activeHotspot === "login-mail" ? " ug-blink-target" : "")}>
              User Mail
            </div>
            <div className="ug-field">Password</div>
            <div className="ug-btn">Get OTP</div>
          </div>
        </div>
      </Screen>
    </section>
  );
}

function SlideSidebar({ activeHotspot }) {
  return (
    <section className="client-guide-section slide-panel" id="sidebar">
      <h2>2. Sidebar</h2>
      <p>
        Use the left menu. Open <strong>Register</strong> for Forms and Reports. That group
        stays open while you are on those pages.
      </p>
      <table className="client-guide-table">
        <thead>
          <tr>
            <th>Menu</th>
            <th>What it is</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Dashboard</td>
            <td>Counts, form chart, and upcoming holidays</td>
          </tr>
          <tr>
            <td>Register → Forms</td>
            <td>View register forms for a specific branch and month.</td>
          </tr>
          <tr>
            <td>Register → Reports</td>
            <td>Register and master reports available for viewing or export.</td>
          </tr>
          <tr>
            <td>Returns</td>
            <td>View and download statutory returns for a period.</td>
          </tr>
          <tr>
            <td>Compliance Documents</td>
            <td>Licences, certificates, and challans.</td>
          </tr>
          <tr>
            <td>User Guide</td>
            <td>This walkthrough</td>
          </tr>
        </tbody>
      </table>
      <Screen
        title="Sidebar"
        caption="Figure 2. AccunityHR Client sidebar."
        hotspotId="sidebar"
        activeHotspot={activeHotspot}
      >
        <AppFrame active="guide">
          <div className={"ug-panel" + (activeHotspot === "sidebar" ? " ug-blink-target" : "")}>
            <h5>AccunityHR</h5>
            <p>The top bar shows AccunityHR. You cannot switch to another organization.</p>
          </div>
        </AppFrame>
      </Screen>
    </section>
  );
}

function SlideDashboard({ activeHotspot }) {
  return (
    <section className="client-guide-section slide-panel" id="dashboard">
      <h2>3. Dashboard</h2>
      <p>
        Open <Link to="/dashboard">Dashboard</Link> after login. Five tiles: Organization,
        Branches, Employee Head Count, New Employees, Exit Employees.
      </p>
      <h3>Registers Overview</h3>
      <ul>
        <li>
          The blue chart is <strong>Form Generated</strong> by month.
        </li>
        <li>The year list only includes years that have form data.</li>
      </ul>
      <h3>Upcoming Holidays</h3>
      <ul>
        <li>This month and next month, across states.</li>
        <li>
          Filter: All / Festival / National. Empty: <strong>No holidays</strong>.
        </li>
      </ul>
      <Screen
        title="Dashboard"
        caption="Figure 3. Dashboard tiles, chart, and holidays."
        hotspotId="dashboard-kpis"
        activeHotspot={activeHotspot}
      >
        <AppFrame active="dash">
          <div
            className={
              "ug-kpis" + (activeHotspot === "dashboard-kpis" ? " ug-blink-target" : "")
            }
          >
            <div className="ug-kpi">
              <b>1</b>
              <span>Organization</span>
            </div>
            <div className="ug-kpi">
              <b>2</b>
              <span>Branches</span>
            </div>
            <div className="ug-kpi">
              <b>128</b>
              <span>Employee Head Count</span>
            </div>
            <div className="ug-kpi">
              <b>4</b>
              <span>New Employees</span>
            </div>
            <div className="ug-kpi">
              <b>1</b>
              <span>Exit Employees</span>
            </div>
          </div>
          <div className="ug-panel">
            <h5>Registers Overview · FY 2025-26</h5>
            <div className="ug-chart" />
          </div>
          <div className="ug-panel">
            <h5>Upcoming Holidays · All Holidays</h5>
            <table className="ug-mini-table">
              <thead>
                <tr>
                  <th>Name of the Festival</th>
                  <th>State</th>
                  <th>Date</th>
                  <th>Type</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Holiday Name</td>
                  <td>Holiday State</td>
                  <td>Holiday Date</td>
                  <td>Festival/National</td>
                </tr>
              </tbody>
            </table>
          </div>
        </AppFrame>
      </Screen>
    </section>
  );
}

function SlideFormsStart({ activeHotspot }) {
  return (
    <section className="client-guide-section slide-panel" id="forms">
      <h2>4. Register → Forms</h2>
      <p>
        Open <Link to="/generated-form">Register → Forms</Link>. Organization is locked —
        only Branch, Year, and Month are selectable.
      </p>
      <Screen
        title="Forms — start"
        caption="Figure 4. AccunityHR locked. Filters empty."
        hotspotId="forms-filters"
        activeHotspot={activeHotspot}
      >
        <AppFrame active="forms">
          <p className="ug-title">
            Select your branch, year, and month to open the related forms.
          </p>
          <OrgCard />
          <div className={activeHotspot === "forms-filters" ? "ug-blink-target" : ""}>
            <FilterBar active="start" />
          </div>
          <div className="ug-empty">Forms will appear here after you click View forms.</div>
        </AppFrame>
      </Screen>
    </section>
  );
}

function SlideFormsBranch({ activeHotspot }) {
  return (
    <section className="client-guide-section slide-panel">
      <h2>4. Forms — Branch</h2>
      <p>The Branch list shows only AccunityHR branches assigned to you.</p>
      <Screen
        title="Forms — Branch list"
        caption="Figure 5. Choose BNG01 - Bengaluru (Kengeri)."
        hotspotId="forms-filters"
        activeHotspot={activeHotspot}
      >
        <AppFrame active="forms">
          <OrgCard />
          <div className={activeHotspot === "forms-filters" ? "ug-blink-target" : ""} style={{ borderRadius: 8 }}>
            <FilterBar active="branch" open="branch" />
          </div>
        </AppFrame>
      </Screen>
    </section>
  );
}

function SlideFormsYear({ activeHotspot }) {
  return (
    <section className="client-guide-section slide-panel">
      <h2>4. Forms — Year</h2>
      <p>Only years that already have related forms are listed.</p>
      <Screen
        title="Forms — Year list"
        caption="Figure 6. Year dropdown. Choose 2026."
        hotspotId="forms-filters"
        activeHotspot={activeHotspot}
      >
        <AppFrame active="forms">
          <OrgCard />
          <div className={activeHotspot === "forms-filters" ? "ug-blink-target" : ""} style={{ borderRadius: 8 }}>
            <FilterBar active="year" open="year" />
          </div>
        </AppFrame>
      </Screen>
    </section>
  );
}

function SlideFormsMonth({ activeHotspot }) {
  return (
    <section className="client-guide-section slide-panel">
      <h2>4. Forms — Month</h2>
      <p>
        Only months with related forms appear. Then click <strong>View forms</strong>.
      </p>
      <Screen
        title="Forms — Month list"
        caption="Figure 7. Choose Aug, then View forms."
        hotspotId="forms-filters"
        activeHotspot={activeHotspot}
      >
        <AppFrame active="forms">
          <OrgCard />
          <div className={activeHotspot === "forms-filters" ? "ug-blink-target" : ""} style={{ borderRadius: 8 }}>
            <FilterBar active="ready" open="month" />
          </div>
        </AppFrame>
      </Screen>
    </section>
  );
}

function SlideFormsList({ activeHotspot }) {
  return (
    <section className="client-guide-section slide-panel">
      <h2>4. Forms — list</h2>
      <p>
        After View forms, the list for <strong>{BRANCH_BNG} · Aug 2026</strong> appears.
      </p>
      <Screen
        title="Forms — list"
        caption="Figure 8. Forms list with eye and download."
        hotspotId="forms-list"
        activeHotspot={activeHotspot}
      >
        <AppFrame active="forms">
          <OrgCard />
          <FilterBar active="list" />
          <div
            className={activeHotspot === "forms-list" ? "ug-blink-target" : ""}
            style={{ borderRadius: 8, padding: 2 }}
          >
            <ListHead title={`${BRANCH_BNG} · Aug 2026`} count="3 forms" />
            <table className="ug-mini-table">
              <thead>
                <tr>
                  <th>
                    <Check />
                  </th>
                  <th>Form</th>
                  <th>Location</th>
                  <th>Month</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <Check />
                  </td>
                  <td>Form XII</td>
                  <td>Bengaluru (Kengeri)</td>
                  <td>Aug 2026</td>
                  <td>
                    <ActionIcons />
                  </td>
                </tr>
                <tr>
                  <td>
                    <Check />
                  </td>
                  <td>Wage Register</td>
                  <td>Bengaluru (Kengeri)</td>
                  <td>Aug 2026</td>
                  <td>
                    <ActionIcons />
                  </td>
                </tr>
                <tr>
                  <td>
                    <Check />
                  </td>
                  <td>Muster Roll</td>
                  <td>Bengaluru (Kengeri)</td>
                  <td>Aug 2026</td>
                  <td>
                    <ActionIcons />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </AppFrame>
      </Screen>
    </section>
  );
}

function SlideFormsPreview({ activeHotspot }) {
  return (
    <section className="client-guide-section slide-panel">
      <h2>4. Forms — View PDF</h2>
      <p>Eye icon opens Form XII preview. Close returns to the list.</p>
      <Screen
        title="Forms — View PDF"
        caption="Figure 9. Form XII preview."
        hotspotId="forms-preview"
        activeHotspot={activeHotspot}
      >
        <div className={"ug-preview" + (activeHotspot === "forms-preview" ? " ug-blink-target" : "")}>
          <div className="ug-preview-bar">
            Form XII
            <button type="button">Close</button>
          </div>
          <div className="ug-preview-body file">
            PDF preview of Form XII for AccunityHR · Bengaluru (Kengeri) · Aug 2026
          </div>
        </div>
      </Screen>
    </section>
  );
}

function SlideFormsActions({ activeHotspot }) {
  return (
    <section className="client-guide-section slide-panel">
      <h2>4. Forms — actions</h2>
      <div className={"ug-callouts" + (activeHotspot === "forms-actions" ? " ug-blink-target" : "")}>
        <div className="ug-callout">
          <span className="ug-ico">👁</span>
          <div>
            <b>Eye — View</b>
            <p>Opens that one form as a PDF preview. Close to go back.</p>
          </div>
        </div>
        <div className="ug-callout">
          <span className="ug-ico">⬇</span>
          <div>
            <b>Download (row)</b>
            <p>Saves that one form. Example: Form XII_BNG01 - Bengaluru (Kengeri).pdf</p>
          </div>
        </div>
        <div className="ug-callout">
          <span className="ug-ico">☐</span>
          <div>
            <b>Checkbox</b>
            <p>Tick rows. Header checkbox selects all.</p>
          </div>
        </div>
        <div className="ug-callout">
          <span className="ug-ico">⬇</span>
          <div>
            <b>Bulk download</b>
            <p>2 or more selected → zip, e.g. BNG01 - Bengaluru (Kengeri)_Aug 2026_3_forms.zip</p>
          </div>
        </div>
      </div>
      <Screen
        title="Forms — bulk selected"
        caption="Figure 10. Two forms ticked for bulk zip."
        hotspotId="forms-actions"
        activeHotspot={activeHotspot}
      >
        <AppFrame active="forms">
          <ListHead title={`${BRANCH_BNG} · Aug 2026`} count="3 forms" bulkOn />
          <table className="ug-mini-table">
            <thead>
              <tr>
                <th>
                  <Check />
                </th>
                <th>Form</th>
                <th>Location</th>
                <th>Month</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <Check on />
                </td>
                <td>Form XII</td>
                <td>Bengaluru (Kengeri)</td>
                <td>Aug 2026</td>
                <td>
                  <ActionIcons />
                </td>
              </tr>
              <tr>
                <td>
                  <Check on />
                </td>
                <td>Wage Register</td>
                <td>Bengaluru (Kengeri)</td>
                <td>Aug 2026</td>
                <td>
                  <ActionIcons />
                </td>
              </tr>
              <tr>
                <td>
                  <Check />
                </td>
                <td>Muster Roll</td>
                <td>Bengaluru (Kengeri)</td>
                <td>Aug 2026</td>
                <td>
                  <ActionIcons />
                </td>
              </tr>
            </tbody>
          </table>
        </AppFrame>
      </Screen>
      <div className="client-guide-note">
        If the file is missing, View shows: “This document isn’t available to view right now.”
      </div>
      <div className="client-guide-warn">
        Only forms generated by the service provider will be available for you to view.
      </div>
    </section>
  );
}

function SlideReportsHub({ activeHotspot }) {
  return (
    <section className="client-guide-section slide-panel" id="reports">
      <h2>5. Register → Reports</h2>
      <ol>
        <li>
          Open <Link to="/report">Register → Reports</Link>.
        </li>
        <li>
          The hub title is <strong>Reports</strong>. <strong>Current month</strong> shows the payroll
          period (for example {REPORT_MONTH_LABEL}).
        </li>
        <li>
          Organization is locked as <strong>{ORG_LABEL}</strong>. You only choose Branch.
        </li>
      </ol>
      <Screen
        title="Reports — hub"
        caption="Figure 12. Reports hub with Current month, Organization, and Branch."
        hotspotId="reports-hub"
        activeHotspot={activeHotspot}
      >
        <ReportsHubMock spot={activeHotspot === "reports-hub" ? "hub" : ""} />
      </Screen>
    </section>
  );
}

function SlideReportsBranch({ activeHotspot }) {
  return (
    <section className="client-guide-section slide-panel">
      <h2>5. Reports — Branch</h2>
      <p>
        Open Branch and choose <strong>{REPORT_BRANCH}</strong>. Cards stay dimmed until a branch is
        selected — same as the live Reports page.
      </p>
      <Screen
        title="Reports — Branch"
        caption="Figure 13. Branch list open on the Reports hub."
        hotspotId="reports-branch"
        activeHotspot={activeHotspot}
      >
        <ReportsHubMock
          spot={activeHotspot === "reports-branch" ? "branch" : ""}
          branchOpen
        />
      </Screen>
    </section>
  );
}

function SlideReportsSearch({ activeHotspot }) {
  return (
    <section className="client-guide-section slide-panel">
      <h2>5. Reports — search</h2>
      <p>
        Search reports by name, code, or category. <strong>Available reports</strong> shows how many
        cards you can open.
      </p>
      <Screen
        title="Reports — search"
        caption="Figure 14. Search reports and Available reports count."
        hotspotId="reports-search"
        activeHotspot={activeHotspot}
      >
        <ReportsHubMock
          spot={activeHotspot === "reports-search" ? "search" : ""}
          branchPicked
          searchText="employee"
        />
      </Screen>
    </section>
  );
}

function SlideReportsCards({ activeHotspot }) {
  return (
    <section className="client-guide-section slide-panel">
      <h2>5. Reports — MONTHLY INPUTS</h2>
      <p>
        Reports are grouped. <strong>MONTHLY INPUTS</strong> holds payroll registers, attendance,
        leave, and monthly employee inputs. Open a card such as Employee Master, Salary Register, or
        CTC Master.
      </p>
      <Screen
        title="Reports — cards"
        caption="Figure 15. MONTHLY INPUTS cards after branch is selected."
        hotspotId="reports-cards"
        activeHotspot={activeHotspot}
      >
        <ReportsHubMock
          spot={activeHotspot === "reports-cards" ? "cards" : ""}
          branchPicked
        />
      </Screen>
    </section>
  );
}

function SlideReportsTable({ activeHotspot }) {
  return (
    <section className="client-guide-section slide-panel">
      <h2>5. Reports — table</h2>
      <p>
        Opening <strong>Employee Master</strong> shows rows for the selected branch and current
        month. Year and Month sit in the table header.
      </p>
      <Screen
        title="Employee Master"
        caption="Figure 16. Employee Master table for AccunityHR Bangalore."
        hotspotId="reports-table"
        activeHotspot={activeHotspot}
      >
        <ReportsTableMock spot={activeHotspot === "reports-table" ? "table" : ""} />
      </Screen>
    </section>
  );
}

function SlideReportsPeriod({ activeHotspot }) {
  return (
    <section className="client-guide-section slide-panel">
      <h2>5. Reports — Year & Month</h2>
      <p>
        Use <strong>Year</strong> then <strong>Month</strong> to change the payroll period. The table
        reloads for that month — the same payroll filter as live Reports.
      </p>
      <Screen
        title="Employee Master — month filter"
        caption="Figure 17. Year and Month filters in the report header."
        hotspotId="reports-period"
        activeHotspot={activeHotspot}
      >
        <ReportsTableMock
          spot={activeHotspot === "reports-period" ? "period" : ""}
          monthOpen
        />
      </Screen>
    </section>
  );
}

function SlideReportsRows({ activeHotspot }) {
  return (
    <section className="client-guide-section slide-panel">
      <h2>5. Reports — search rows</h2>
      <p>
        Use <strong>Search here...</strong> to find an employee by name or code. Example: Asha.
      </p>
      <Screen
        title="Employee Master — search"
        caption="Figure 18. Table filtered to matching employees."
        hotspotId="reports-rows"
        activeHotspot={activeHotspot}
      >
        <ReportsTableMock
          spot={activeHotspot === "reports-rows" ? "rows" : ""}
          searchText="Asha"
          filterAsha
        />
      </Screen>
    </section>
  );
}

function SlideReturnsFilters({ activeHotspot }) {
  return (
    <section className="client-guide-section slide-panel" id="returns">
      <h2>6. Returns — filters</h2>
      <p>
        Open <Link to="/returns">Returns</Link>. Same flow as Forms; button is{" "}
        <strong>View documents</strong>.
      </p>
      <Screen
        title="Returns — filters"
        caption="Figure 13. Branch list open for Returns."
        hotspotId="returns-filters"
        activeHotspot={activeHotspot}
      >
        <AppFrame active="returns">
          <p className="ug-title">
            Select your branch, year, and month to open the related returns.
          </p>
          <OrgCard />
          <div
            className={activeHotspot === "returns-filters" ? "ug-blink-target" : ""}
            style={{ borderRadius: 8 }}
          >
            <FilterBar active="branch" open="branch" submit="View Documents" />
          </div>
        </AppFrame>
      </Screen>
    </section>
  );
}

function SlideReturnsList({ activeHotspot }) {
  return (
    <section className="client-guide-section slide-panel">
      <h2>6. Returns — list</h2>
      <p>PF Return and ESIC Return with eye, download, and bulk zip.</p>
      <Screen
        title="Returns — list"
        caption="Figure 16–17. Returns list."
        hotspotId="returns-list"
        activeHotspot={activeHotspot}
      >
        <AppFrame active="returns">
          <div
            className={activeHotspot === "returns-list" ? "ug-blink-target" : ""}
            style={{ borderRadius: 8, padding: 2 }}
          >
            <ListHead title={`${BRANCH_BNG} · Aug 2026`} count="2 returns" bulkOn />
            <table className="ug-mini-table">
              <thead>
                <tr>
                  <th>
                    <Check />
                  </th>
                  <th>Document</th>
                  <th>Location</th>
                  <th>Month</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <Check on />
                  </td>
                  <td>PF Return</td>
                  <td>Bengaluru (Kengeri)</td>
                  <td>Aug 2026</td>
                  <td>
                    <ActionIcons />
                  </td>
                </tr>
                <tr>
                  <td>
                    <Check on />
                  </td>
                  <td>ESIC Return</td>
                  <td>Bengaluru (Kengeri)</td>
                  <td>Aug 2026</td>
                  <td>
                    <ActionIcons />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </AppFrame>
      </Screen>
    </section>
  );
}

function SlideDocuments({ activeHotspot }) {
  return (
    <section className="client-guide-section slide-panel" id="documents">
      <h2>7. Compliance Documents</h2>
      <p>
        Open <Link to="/compliance-documents">Compliance Documents</Link> for licences,
        certificates, and challans. Same steps: locked org → Branch → Year → Month → View
        documents.
      </p>
      <Screen
        title="Documents — list"
        caption="Figure 16. Document list."
        hotspotId="documents"
        activeHotspot={activeHotspot}
      >
        <AppFrame active="docs">
          <div
            className={activeHotspot === "documents" ? "ug-blink-target" : ""}
            style={{ borderRadius: 8, padding: 2 }}
          >
            <ListHead title={`${BRANCH_BNG} · Aug 2026`} count="3 documents" />
            <table className="ug-mini-table">
              <thead>
                <tr>
                  <th>
                    <Check />
                  </th>
                  <th>Document</th>
                  <th>Location</th>
                  <th>Month</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <Check />
                  </td>
                  <td>Trade licence</td>
                  <td>Bengaluru (Kengeri)</td>
                  <td>Aug 2026</td>
                  <td>
                    <ActionIcons />
                  </td>
                </tr>
                <tr>
                  <td>
                    <Check />
                  </td>
                  <td>PF challan</td>
                  <td>Bengaluru (Kengeri)</td>
                  <td>Aug 2026</td>
                  <td>
                    <ActionIcons />
                  </td>
                </tr>
                <tr>
                  <td>
                    <Check />
                  </td>
                  <td>ESIC challan</td>
                  <td>Bengaluru (Kengeri)</td>
                  <td>Aug 2026</td>
                  <td>
                    <ActionIcons />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </AppFrame>
      </Screen>
      <div className="client-guide-warn">
        You cannot upload, edit, or delete the documents. Your administrator does that — you
        can only view.
      </div>
    </section>
  );
}

function SlideSignout({ activeHotspot }) {
  return (
    <section className="client-guide-section slide-panel" id="signout">
      <h2>8. Sign out</h2>
      <p>Use the profile menu in the top bar to sign out of AccunityHR.</p>
      <div
        className={
          "client-guide-note" + (activeHotspot === "signout" ? " ug-blink-target" : "")
        }
      >
        Guide tour completed. Click <strong>Finish</strong> to open the full guide, or use
        Back to review a slide.
      </div>
    </section>
  );
}

function FullGuide() {
  return (
    <>
      <SlideLogin activeHotspot="" />
      <SlideSidebar activeHotspot="" />
      <SlideDashboard activeHotspot="" />
      <SlideFormsStart activeHotspot="" />
      <SlideFormsBranch activeHotspot="" />
      <SlideFormsYear activeHotspot="" />
      <SlideFormsMonth activeHotspot="" />
      <SlideFormsList activeHotspot="" />
      <SlideFormsPreview activeHotspot="" />
      <SlideFormsActions activeHotspot="" />
      <SlideReportsHub activeHotspot="" />
      <SlideReportsBranch activeHotspot="" />
      <SlideReportsSearch activeHotspot="" />
      <SlideReportsCards activeHotspot="" />
      <SlideReportsTable activeHotspot="" />
      <SlideReportsPeriod activeHotspot="" />
      <SlideReportsRows activeHotspot="" />
      <SlideReturnsFilters activeHotspot="" />
      <SlideReturnsList activeHotspot="" />
      <SlideDocuments activeHotspot="" />
      <SlideSignout activeHotspot="" />
    </>
  );
}

function renderSlide(id, onStart, activeHotspot, onJump) {
  switch (id) {
    case "intro":
      return <SlideIntro onStart={onStart} activeHotspot={activeHotspot} onJump={onJump} />;
    case "login":
      return <SlideLogin activeHotspot={activeHotspot} />;
    case "sidebar":
      return <SlideSidebar activeHotspot={activeHotspot} />;
    case "dashboard":
      return <SlideDashboard activeHotspot={activeHotspot} />;
    case "forms-start":
      return <SlideFormsStart activeHotspot={activeHotspot} />;
    case "forms-branch":
      return <SlideFormsBranch activeHotspot={activeHotspot} />;
    case "forms-year":
      return <SlideFormsYear activeHotspot={activeHotspot} />;
    case "forms-month":
      return <SlideFormsMonth activeHotspot={activeHotspot} />;
    case "forms-list":
      return <SlideFormsList activeHotspot={activeHotspot} />;
    case "forms-preview":
      return <SlideFormsPreview activeHotspot={activeHotspot} />;
    case "forms-actions":
      return <SlideFormsActions activeHotspot={activeHotspot} />;
    case "reports":
    case "reports-hub":
      return <SlideReportsHub activeHotspot={activeHotspot} />;
    case "reports-branch":
      return <SlideReportsBranch activeHotspot={activeHotspot} />;
    case "reports-search":
      return <SlideReportsSearch activeHotspot={activeHotspot} />;
    case "reports-cards":
      return <SlideReportsCards activeHotspot={activeHotspot} />;
    case "reports-table":
      return <SlideReportsTable activeHotspot={activeHotspot} />;
    case "reports-period":
      return <SlideReportsPeriod activeHotspot={activeHotspot} />;
    case "reports-rows":
      return <SlideReportsRows activeHotspot={activeHotspot} />;
    case "returns-filters":
      return <SlideReturnsFilters activeHotspot={activeHotspot} />;
    case "returns-list":
      return <SlideReturnsList activeHotspot={activeHotspot} />;
    case "documents":
      return <SlideDocuments activeHotspot={activeHotspot} />;
    case "signout":
      return <SlideSignout activeHotspot={activeHotspot} />;
    default:
      return null;
  }
}

function moduleForSlide(slideId) {
  return CHECKLIST.find((m) => m.slideIds.includes(slideId)) || null;
}

function ClientUserGuide() {
  const { startTour: startLiveTour, active: liveTourActive } = useClientTour();
  const saved = loadTourState();
  const [mode, setMode] = useState("full");
  const [index, setIndex] = useState(0);
  const [showWelcome, setShowWelcome] = useState(false);
  const [showDone, setShowDone] = useState(false);
  const [checklistOpen, setChecklistOpen] = useState(true);
  const [completedModules, setCompletedModules] = useState(saved?.completedModules || []);
  const [pendingScrollId, setPendingScrollId] = useState(null);

  const slide = SLIDES[index];
  const total = SLIDES.length;
  const progress = Math.round(((index + 1) / total) * 100);
  const isLast = index >= total - 1;
  const isFirst = index <= 0;
  const activeModule = moduleForSlide(slide.id);

  const checklistItems = useMemo(() => {
    return CHECKLIST.map((m) => {
      let status = "todo";
      if (completedModules.includes(m.id)) status = "done";
      else if (activeModule?.id === m.id && mode === "deck") status = "current";
      return { ...m, status };
    });
  }, [completedModules, activeModule, mode]);

  useEffect(() => {
    if (mode !== "deck" || !activeModule) return;
    const lastSlide = activeModule.slideIds[activeModule.slideIds.length - 1];
    if (slide.id === lastSlide && !completedModules.includes(activeModule.id)) {
      const next = [...completedModules, activeModule.id];
      setCompletedModules(next);
      saveTourState({ completedModules: next });
    }
  }, [slide.id, activeModule, completedModules, mode]);

  const goNext = () => {
    if (isLast) {
      setShowDone(true);
      saveTourState({ completed: true, completedModules });
      return;
    }
    setIndex((i) => i + 1);
    saveTourState({ index: index + 1 });
  };

  const goPrev = () => {
    if (mode === "full") {
      setMode("deck");
      setIndex(total - 1);
      return;
    }
    if (!isFirst) {
      setIndex((i) => i - 1);
      saveTourState({ index: index - 1 });
    }
  };

  const skipToFull = () => {
    setMode("full");
    setShowWelcome(false);
    saveTourState({ liveDismissed: true, dismissed: true });
  };

  const startTour = () => {
    setShowWelcome(false);
    setShowDone(false);
    saveTourState({ liveDismissed: false, dismissed: false });
    startLiveTour();
  };

  const restartDeck = () => {
    setShowDone(false);
    setShowWelcome(false);
    saveTourState({ completed: false, dismissed: false, liveDismissed: false });
    startLiveTour();
  };

  const jumpModule = (moduleId) => {
    const mod = CHECKLIST.find((m) => m.id === moduleId);
    if (!mod) return;
    const slideIndex = SLIDES.findIndex((s) => s.id === mod.slideIds[0]);
    if (slideIndex < 0) return;
    setMode("deck");
    setIndex(slideIndex);
    setShowWelcome(false);
    setShowDone(false);
  };

  const jumpToSection = (sectionId) => {
    setShowWelcome(false);
    setShowDone(false);
    if (mode !== "full") {
      setMode("full");
      setPendingScrollId(sectionId);
      return;
    }
    scrollToGuideSection(sectionId);
  };

  useEffect(() => {
    if (mode !== "full" || !pendingScrollId) return;
    const id = pendingScrollId;
    setPendingScrollId(null);
    const timer = window.setTimeout(() => scrollToGuideSection(id), 50);
    return () => window.clearTimeout(timer);
  }, [mode, pendingScrollId]);

  return (
    <div className="client-guide-page">
      <div className="client-guide-shell">
        {!liveTourActive && (
          <header className="client-guide-hero">
            <h1>Client user guide</h1>
            <p>
              Start the live AccunityHR client tour. Each area is explained when you reach it —
              Forms, Reports, Returns and Documents use AccunityHR · Marketing Strategies demo
              data.
            </p>
            <div className="client-guide-toolbar">
              <button type="button" onClick={startTour}>
                Start live tour
              </button>
              <button type="button" className="ghost" onClick={() => setShowWelcome(true)}>
                About the tour
              </button>
            </div>
            <GuideToc onJump={jumpToSection} />
          </header>
        )}

        {mode === "deck" && !showWelcome && !liveTourActive && (
          <div className="tour-coach">
            <div className="tour-coach-bar">
              <div className="tour-coach-fill" style={{ width: progress + "%" }} />
            </div>
            <div className="tour-coach-row">
              <div className="tour-coach-text">
                <span className="tour-coach-step">
                  {index + 1}/{total}
                </span>
                <strong>{slide.title}</strong>
                <span>{slide.hint}</span>
              </div>
              <div className="tour-coach-actions">
                <button type="button" className="ghost" disabled={isFirst} onClick={goPrev}>
                  Previous
                </button>
                <button type="button" className="ghost" onClick={skipToFull}>
                  Skip
                </button>
                {index === 0 ? (
                  <button type="button" onClick={startTour}>
                    Start live tour
                  </button>
                ) : (
                  <button type="button" onClick={goNext}>
                    {isLast ? "Finish" : "Next"}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {!liveTourActive && (
          <div className={mode === "deck" ? "slide-stage" : ""}>
            {mode === "deck"
              ? renderSlide(slide.id, startTour, slide.hotspot, jumpToSection)
              : <FullGuide />}
          </div>
        )}

        {mode === "deck" && !showWelcome && !liveTourActive && (
          <TourTooltip
            title={slide.tooltipTitle}
            body={slide.tooltipBody}
            stepLabel={`${index + 1} of ${total}`}
            onBack={goPrev}
            onNext={index === 0 ? startTour : goNext}
            onSkip={skipToFull}
            nextLabel={index === 0 ? "Start live tour" : isLast ? "Finish" : "Next"}
            backDisabled={isFirst}
          />
        )}

        {mode === "deck" && !showWelcome && !liveTourActive && (
          <TourChecklist
            items={checklistItems}
            activeModuleId={activeModule?.id}
            onJump={jumpModule}
            collapsed={!checklistOpen}
            onToggle={() => setChecklistOpen((v) => !v)}
          />
        )}
      </div>

      {showWelcome && !liveTourActive && (
        <WelcomeModal
          onStart={startTour}
          onSkip={() => {
            setShowWelcome(false);
            setMode("full");
            saveTourState({ liveDismissed: true, dismissed: true });
          }}
        />
      )}

      {showDone && (
        <DoneModal
          onRestart={restartDeck}
          onFullGuide={() => {
            setShowDone(false);
            setMode("full");
          }}
          onClose={() => {
            setShowDone(false);
            setMode("full");
          }}
        />
      )}
    </div>
  );
}

export default ClientUserGuide;